import {
  Component,
  OnInit,
  input,
  InputSignal,
  signal,
  WritableSignal,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { ManagementListItem } from '@/app/modules/management-list/management-list.component';

/**
 * This component is used to manage FilterProfiles by renaming,
 * deleting or setting them as default.
 */
@Component({
  selector: 'rdo-management-list-row',
  template: `
    @if (group && !group.get('isDeleted').value) {
      <div class="management-list-row" [formGroup]="group">
        <div class="name-container">
          @if (!isEditingName()) {
            <span class="name-container">{{ group.get('name').value }}</span>
          }
          @if (isEditingName()) {
            <input
              #nameInput
              type="text"
              formControlName="name"
              class="profile-name-input"
              maxlength="50"
              (blur)="onEditNameComplete()"
              (keydown.enter)="onEditNameComplete()"
            />
          }
          <button (click)="onEditName()" class="btn">
            @if (!isEditingName()) {
              <i class="management-list-icon fa fa-pencil"></i>
            }
          </button>
        </div>
        @if (group) {
          <div class="actions-container" [formGroup]="group">
            <mat-checkbox class="default-checkbox" formControlName="isDefault">
            </mat-checkbox>
            @if (data().canDelete) {
              <button (click)="onDelete()" class="btn">
                <i class="management-list-icon fa fa-trash"></i>
              </button>
            }
          </div>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }
    .management-list-row {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      gap: 30px;
    }
    .name-container {
      font-size: 1.6rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
      min-width: 0;
    }
    .actions-container {
      display: flex;
      align-items: center;
      font-weight: bold;
      font-size: 1.5rem;
      flex-shrink: 0;
      justify-content: space-between;
    }
    .default-checkbox {
    }
    .management-list-icon {
      color: #5a5a5a;
      cursor: pointer;
    }
    .btn {
      font-size: 2.3rem;
      border: none;
      background: none;
      &:hover {
        .icon {
          color: red;
        }
      }
    }
  `,
  standalone: true,
  providers: [TranslateService],
  imports: [MatCheckbox, ReactiveFormsModule],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ManagementListRowComponent implements OnInit {
  @ViewChild('nameInput') nameInput: ElementRef<HTMLInputElement>;

  public form: InputSignal<FormGroup> = input(null);
  public data: InputSignal<ManagementListItem> = input(null);
  public formKey: InputSignal<string> = input('');
  public isEditingName: WritableSignal<boolean> = signal(false);

  public group: FormGroup<TODO>;

  constructor() {}

  ngOnInit(): void {
    this.group = this.buildFormGroup();
    const formGroup = this.form();
    if (formGroup && !formGroup.contains(this.formKey())) {
      formGroup.addControl(this.formKey(), this.group);
    }

    // Subscribe to value changes to mark the form as dirty
    this.group.valueChanges.subscribe(() => {
      if (!this.group.dirty) {
        this.group.markAsDirty();
      }
    });
  }

  private buildFormGroup(): FormGroup {
    const data = this.data();
    const group = new FormGroup({
      name: new FormControl(data.name, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isDefault: new FormControl(data.isDefault),
      isDeleted: new FormControl(false),
    });
    return group;
  }

  public onDelete() {
    const el = this.group.get('isDeleted');
    el.setValue(!el.value);
    this.group.markAsDirty();
  }

  public onEditNameComplete() {
    this.nameInput?.nativeElement.blur();
    this.isEditingName.set(false);
    // Ensure the form group is marked as dirty if the name control is dirty
    const nameControl = this.group.get('name');
    if (nameControl && nameControl.dirty) {
      this.group.markAsDirty();
    }
  }

  public onEditName() {
    this.isEditingName.set(true);
    // Focus the input after Angular renders it
    setTimeout(() => {
      this.nameInput?.nativeElement.focus();
      this.nameInput?.nativeElement.select();
    });
  }
}
